/**
 * Behaviour for the PDF plan product page (`sections/plan-product.liquid`).
 *
 * Deliberately dependency-free: no `@theme/*` bare specifiers, so the module can
 * be loaded straight from the section without an entry in the import map in
 * `snippets/scripts.liquid`. It only touches its own markup — the add to cart
 * flow stays with `product-form-component`, which reads the hidden
 * `properties[...]` inputs this file keeps in sync.
 */

/**
 * Diet Preference + Lifestyle chip groups.
 *
 * Both write to a hidden input (`data-plan-option-input="<key>"`), so the
 * selection posts as a line item property with the add to cart form.
 *
 * The Diet Plan is also sold as one variant per plan, and the PDF sent is the
 * one of the VARIANT on the order - so every pick also puts the matching
 * variant into the form's `id` (map from sections/plan-product.liquid). On a
 * single-variant product `id` is left untouched; see #syncVariant for a pick
 * with no variant on a product that has the plan options.
 */
class PlanOptionsComponent extends HTMLElement {
  /** @type {{ variants: Record<string, number>, hasPlanOptions: boolean, fallbackVariantId: number | null } | null} */
  #plan = null;
  /** True while connecting: the URL is only written for her picks, as the stock variant picker does. */
  #connecting = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    // The page renders the first picks selected. A ?variant= in the link (a shared link, a
    // product card) picks the chips of that plan instead; then the variant is made to agree
    // with the chips, so what she sees is what she buys. The URL is left as it was opened.
    this.#connecting = true;
    this.#selectFromUrl();
    this.#syncVariant();
    this.#connecting = false;
  }

  /**
   * True on the PDP; false in a dialog (the quick-add modal on a collection page, from
   * assets/quick-add.js), where the URL is the page's, not this plan's.
   */
  get #ownsUrl() {
    return !this.closest('dialog');
  }

  /** Selects the chips of the plan named by ?variant=, when it is one of the plan variants. */
  #selectFromUrl() {
    if (!this.#ownsUrl) return;
    const variantId = Number(new URL(window.location.href).searchParams.get('variant'));
    if (!variantId) return;
    const key = Object.keys(this.#planData().variants).find((pick) => this.#planData().variants[pick] === variantId);
    if (!key) return;
    const [diet, lifestyle] = key.split('|');
    for (const [optionKey, value] of [['diet', diet], ['lifestyle', lifestyle]]) {
      const chip = [...this.querySelectorAll(`[data-plan-option="${optionKey}"]`)].find(
        (control) => control instanceof HTMLElement && control.dataset.planValue === value
      );
      if (chip instanceof HTMLElement) this.#select(chip);
    }
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const option = /** @type {HTMLElement | null | undefined} */ (target?.closest('[data-plan-option]'));
    if (option) this.#select(option);
  };

  /**
   * Applies a picked value: updates the hidden input, the inline label and the
   * checked state of every chip in the same group.
   *
   * @param {HTMLElement} option
   */
  #select(option) {
    const key = option.dataset.planOption;
    const value = option.dataset.planValue;
    if (!key || value == null) return;

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-plan-option-input="${key}"]`));
    if (input) input.value = value;

    for (const output of this.querySelectorAll(`[data-plan-option-output="${key}"]`)) {
      output.textContent = value;
    }

    for (const control of this.querySelectorAll(`[data-plan-option="${key}"]`)) {
      const isSelected = control === option;
      control.classList.toggle('is-selected', isSelected);
      control.setAttribute('aria-checked', String(isSelected));
    }

    this.#syncVariant();
  }

  /** The plan variants block from sections/plan-product.liquid, read once from the page. */
  #planData() {
    if (this.#plan) return this.#plan;
    const script = this.querySelector('script[data-plan-variants]');
    try {
      const data = (script && JSON.parse(script.textContent || '{}')) || {};
      this.#plan = {
        variants: data.variants || {},
        hasPlanOptions: data.hasPlanOptions === true,
        fallbackVariantId: data.fallbackVariantId || null,
      };
    } catch (error) {
      // A broken block must not break the page: keep the product's own variant.
      console.warn('plan-product: unreadable plan variants', error);
      this.#plan = { variants: {}, hasPlanOptions: false, fallbackVariantId: null };
    }
    return this.#plan;
  }

  /**
   * Puts the variant matching the current picks into the add to cart form.
   *
   * A product with one variant has no plan options: its id is left alone, as before.
   * A product WITH the options but no variant for this pick has a spelling mismatch in
   * Shopify. Then the earlier pick's variant must not stay in the form (the PDF sent is
   * the variant's), so the first variant goes in instead — the backend sends the PDF of
   * her picks for it — and the mismatch is logged.
   */
  #syncVariant() {
    const diet = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-plan-option-input="diet"]'));
    const lifestyle = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-plan-option-input="lifestyle"]'));
    if (!diet || !lifestyle) return;
    const plan = this.#planData();
    const idInput = /** @type {HTMLInputElement | null} */ (this.closest('form')?.querySelector('input[name="id"]'));
    const variantId = plan.variants[`${diet.value}|${lifestyle.value}`];
    if (variantId) {
      if (idInput) idInput.value = String(variantId);
      this.#updateUrl(variantId);
      return;
    }
    if (!plan.hasPlanOptions) return;
    if (idInput && plan.fallbackVariantId) idInput.value = String(plan.fallbackVariantId);
    console.warn('plan-product: no variant for this pick, using the first variant', {
      diet: diet.value,
      lifestyle: lifestyle.value,
    });
  }

  /**
   * Keeps ?variant= on the picked plan, like the stock variant picker does, so a copied or
   * shared link opens on the same plan. Only on her picks, not on page load, so an opened link
   * stays as it was. replaceState: a pick is not a page to go back to.
   *
   * @param {number} variantId
   */
  #updateUrl(variantId) {
    if (this.#connecting || !this.#ownsUrl) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variantId)) return;
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState(window.history.state, '', url.toString());
  }
}

/** Tap-to-zoom on the plan artwork. */
class PlanMediaComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #overlay = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-plan-expand]')) return;
    this.#open();
  };

  #open() {
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('.plan-pdp__image'));
    if (!image || this.#overlay) return;

    const overlay = document.createElement('div');
    overlay.className = 'plan-pdp__zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const zoomed = document.createElement('img');
    zoomed.src = image.currentSrc || image.src;
    zoomed.alt = image.alt;
    overlay.appendChild(zoomed);

    overlay.addEventListener('click', () => this.#close());
    document.addEventListener('keydown', this.#handleKeydown);
    document.body.appendChild(overlay);
    this.#overlay = overlay;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') this.#close();
  };

  #close() {
    this.#overlay?.remove();
    this.#overlay = null;
    document.removeEventListener('keydown', this.#handleKeydown);
  }
}

if (!customElements.get('plan-options-component')) {
  customElements.define('plan-options-component', PlanOptionsComponent);
}

if (!customElements.get('plan-media-component')) {
  customElements.define('plan-media-component', PlanMediaComponent);
}

// Share button — Web Share where available, clipboard elsewhere.
document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = /** @type {HTMLElement | null} */ (target.closest('[data-plan-share]'));
  if (!button) return;

  const url = button.dataset.shareUrl || window.location.href;
  const title = button.dataset.shareTitle || document.title;

  try {
    if (navigator.share) {
      await navigator.share({ title, url });
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(url);
    }
  } catch {
    // The person dismissed the share sheet, or the clipboard was blocked.
  }
});

/**
 * Sticky bar: keep it pinned on pages too short for the shared component to ever reveal it.
 *
 * These pages behave like every other PDP - below 750px the shared stylesheet pins the bar from
 * page load and it stays put while the in-page Add to Cart is on screen, exactly as the hair serum
 * page does. Nothing here hides it.
 *
 * The one gap is desktop. Above 750px the shared component only sets `data-stuck` once the buy
 * block has scrolled clear ABOVE the viewport, and both plan pages are short - roughly 1250px
 * against a ~1150px viewport, so 80-130px of scroll. The block never clears the top edge, so the
 * bar would never appear at all. When that is the case the bar is pinned instead, via
 * `data-plan-always-on` rather than by writing `data-stuck`, which the shared component owns and
 * would immediately reset through its own observer.
 */
const planStickyBar = document.querySelector('sticky-add-to-cart');
const planBuyButtons = document.querySelector('.plan-pdp__buy-buttons');

if (planStickyBar && planBuyButtons) {
  /** @returns {boolean} true when scrolling can push the buy block past the top of the viewport. */
  const buyButtonsCanLeaveViewport = () => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const buyButtonsDocumentBottom = planBuyButtons.getBoundingClientRect().bottom + window.scrollY;
    return maxScroll >= buyButtonsDocumentBottom;
  };

  const applyPinnedFallback = () => {
    planStickyBar.toggleAttribute('data-plan-always-on', !buyButtonsCanLeaveViewport());
  };

  applyPinnedFallback();

  // The page grows as lazy sections and images land, and rotating a phone changes both terms of
  // the comparison, so the decision cannot be made once at startup.
  window.addEventListener('resize', applyPinnedFallback, { passive: true });
  window.addEventListener('load', applyPinnedFallback);
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(applyPinnedFallback).observe(document.documentElement);
  }
}
